import React, { useState, useCallback } from 'react';
import { Upload, X, Image as ImageIcon, Check } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ImageUploader = ({ onImagesUploaded, maxImages = 5, existingImages = [] }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadedImages, setUploadedImages] = useState(existingImages);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState(null);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback(async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      await uploadFiles(e.dataTransfer.files);
    }
  }, [uploadedImages]);

  const handleFileInput = async (e) => {
    if (e.target.files && e.target.files.length > 0) {
      await uploadFiles(e.target.files);
    }
  };

  const uploadFiles = async (files) => {
    if (uploadedImages.length + files.length > maxImages) {
      setError(`Maximum ${maxImages} images allowed`);
      return;
    }

    setUploading(true);
    setError(null);

    try {
      const formData = new FormData();
      Array.from(files).forEach((file) => {
        formData.append('files', file);
      });

      const { data } = await axios.post(
        `${API}/upload/images`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          withCredentials: true,
        }
      );

      const newImages = [...uploadedImages, ...data.urls];
      setUploadedImages(newImages);
      onImagesUploaded(newImages);
    } catch (err) {
      const errorDetail = err.response?.data?.detail || 'Upload failed';
      
      // Provide helpful message for Cloudinary configuration error
      if (errorDetail.includes('not configured') || errorDetail.includes('api_key')) {
        setError('⚠️ Image upload not configured. Administrator: Please add Cloudinary credentials to backend environment. See /app/memory/cloudinary-setup-guide.md for setup instructions.');
      } else {
        setError(errorDetail);
      }
      
      if (process.env.NODE_ENV === 'development') {
        console.error('Upload error:', err);
      }
    } finally {
      setUploading(false);
    }
  };

  const removeImage = async (index) => {
    const imageUrl = uploadedImages[index];
    
    try {
      // Delete from server
      await axios.delete(`${API}/upload/image`, {
        params: { image_url: imageUrl },
        withCredentials: true,
      });
      
      // Remove from state
      const newImages = uploadedImages.filter((_, i) => i !== index);
      setUploadedImages(newImages);
      onImagesUploaded(newImages);
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Delete error:', err);
      }
    }
  };

  // Image reordering handlers
  const handleImageDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleImageDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleImageDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleImageDrop = (e, dropIndex) => {
    e.preventDefault();
    
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    // Reorder images array
    const newImages = [...uploadedImages];
    const draggedImage = newImages[draggedIndex];
    
    // Remove from old position
    newImages.splice(draggedIndex, 1);
    // Insert at new position
    newImages.splice(dropIndex, 0, draggedImage);
    
    setUploadedImages(newImages);
    onImagesUploaded(newImages);
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleImageDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  return (
    <div className="space-y-4">
      {/* Upload Area */}
      {uploadedImages.length < maxImages && (
        <div
          className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
            dragActive
              ? 'border-orange-500 bg-orange-50'
              : 'border-gray-300 hover:border-orange-400'
          }`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <input
            type="file"
            id="file-upload"
            className="hidden"
            multiple
            accept="image/*"
            onChange={handleFileInput}
            disabled={uploading}
          />
          
          {uploading ? (
            <div className="flex flex-col items-center">
              <div className="w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mb-4" />
              <p className="text-gray-600">Uploading to Cloudinary...</p>
            </div>
          ) : (
            <div className="flex flex-col items-center">
              <Upload className="w-12 h-12 text-orange-500 mb-4" />
              <p className="text-lg font-medium text-gray-900 mb-2">
                Drag & Drop Images Here
              </p>
              <p className="text-sm text-gray-500 mb-4">
                or
              </p>
              <label
                htmlFor="file-upload"
                className="inline-flex items-center px-6 py-3 bg-orange-500 hover:bg-orange-600 text-white font-medium rounded-lg cursor-pointer transition-colors"
              >
                <Upload className="w-5 h-5 mr-2" />
                Browse Files from Computer
              </label>
              <p className="text-xs text-gray-500 mt-4">
                PNG, JPG, WEBP up to 10MB • {uploadedImages.length}/{maxImages} images uploaded
              </p>
            </div>
          )}
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* Uploaded Images Grid */}
      {uploadedImages.length > 0 && (
        <div>
          <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
            <Check className="w-5 h-5 text-green-600" />
            Uploaded Images ({uploadedImages.length})
          </h4>
          <p className="text-sm text-gray-600 mb-3">
            💡 <strong>Tip:</strong> Drag and drop images to reorder. First image is the primary product image.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {uploadedImages.map((url, index) => (
              <Card 
                key={url} 
                className={`relative group overflow-hidden transition-all cursor-move ${
                  draggedIndex === index ? 'opacity-50 scale-95' : ''
                } ${
                  dragOverIndex === index ? 'ring-2 ring-orange-500 scale-105' : ''
                }`}
                draggable
                onDragStart={(e) => handleImageDragStart(e, index)}
                onDragOver={(e) => handleImageDragOver(e, index)}
                onDragLeave={handleImageDragLeave}
                onDrop={(e) => handleImageDrop(e, index)}
                onDragEnd={handleImageDragEnd}
              >
                <CardContent className="p-2">
                  <div className="relative aspect-square">
                    <img
                      src={url}
                      alt={`Uploaded ${index + 1}`}
                      className="w-full h-full object-cover rounded"
                      draggable={false}
                    />
                    <button
                      onClick={() => removeImage(index)}
                      className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600 z-10"
                      title="Remove image"
                      type="button"
                    >
                      <X className="w-4 h-4" />
                    </button>
                    {index === 0 && (
                      <div className="absolute bottom-2 left-2 bg-orange-500 text-white text-xs px-2 py-1 rounded font-medium">
                        Primary
                      </div>
                    )}
                    {/* Drag handle indicator */}
                    <div className="absolute top-2 left-2 bg-gray-800 bg-opacity-60 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity">
                      ⋮⋮ Drag
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageUploader;
